/* eslint-disable react/no-array-index-key */
import React, { useMemo } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import {
    NSTable,
    NSCellInput,
} from 'bricks';
import { tableValueClassName, WattsType } from 'views/ProFormaTable/constants';
import { ProFormaModelIds, PRO_FORMA_MODELS_WITH_LEASING_COST_RESERVES } from 'constants/proForma';
import {
    IRecoveryEstimate, IPatchOperatingInfoData,
} from 'views/ProFormaTable/types';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';
import './EstimateRecoveryTable.scss';

export interface IEstimateRecoveryTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const EstimateRecoveryTable = ({
    operationBeginIndex, baseCashFlowColumns,
}: IEstimateRecoveryTableProps) => {
    const {
        incomeSummary, handleUpdateProFormaOperations, operatingInfo, proForma,
        showCents, formatDisplayValue, operationsCashFlow, wattsType,
    } = useProFormaSocketContext();

    const handleUpdate = (
        dataKey: keyof IPatchOperatingInfoData,
    ) => (e: React.ChangeEvent<HTMLInputElement>) => {
        handleUpdateProFormaOperations(operatingInfo?.id, {
            [dataKey]: e.target.value,
        });
    };

    const isApartment = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proForma.proFormaModelId);
    const isCommercial = PRO_FORMA_MODELS_WITH_LEASING_COST_RESERVES.includes(proForma.proFormaModelId);
    const areaType = isCommercial || isApartment ? 'RSF' : 'SF';

    const defaultRecoveryEstimate = {
        squareFootage: 0,
        recoveryPercentage: null,
        recoveryValuePerKwPerYear: null,
        recoveryValuePerSfPerYear: null,
        proRataShare: 0,
        recoveryAmountPerYear: null,
    };

    const data = useMemo(
        () => [incomeSummary.recoveryEstimate ?? defaultRecoveryEstimate],
        [incomeSummary.recoveryEstimate],
    );

    const recoveryCashFlowColumns: ColumnDef<IRecoveryEstimate>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `estimate-recovery-period-${column.id}`,
        accessorFn: () => (
            typeof operationsCashFlow?.[operationBeginIndex]?.recoveryEstimate === 'number'
                ? (operationsCashFlow?.[cashFlowIndex]?.recoveryEstimate || 0)
                : null
        ),
        footer: () => (
            <span className="text-dark">
                {operationBeginIndex
                    ? formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.totalRecoveryIncome || 0, showCents, 'currency')
                    : ' '}
            </span>
        ),
    }));

    let MODEL_SPECIFIC_COLUMN: ColumnDef<IRecoveryEstimate> = {
        accessorKey: 'recoveryValuePerSfPerYear',
        id: 'recoveryValuePerSfPerYear',
        header: () => <span>{`$/${areaType}/year`}</span>,
        cell: ({ row, getValue }) => (
            <span className={tableValueClassName(getValue())}>
                {formatDisplayValue(getValue(), true, 'currency')}
            </span>
        ),
        footer: () => <span>{formatDisplayValue(incomeSummary.totalRecoveryIncome?.totalValuePerSfPerYear || 0, true, 'currency')}</span>,
    };
    if (proForma.proFormaModelId === ProFormaModelIds.DATA_CENTER) {
        if (wattsType === WattsType.MEGAWATTS) {
            MODEL_SPECIFIC_COLUMN = {
                accessorKey: 'recoveryValuePerMwPerYear',
                id: 'recoveryValuePerMwPerYear',
                header: () => <span>$/MW/year</span>,
                cell: ({ row, getValue }) => (
                    <span className={tableValueClassName(getValue())}>
                        {formatDisplayValue(getValue(), true, 'currency')}
                    </span>
                ),
                footer: () => <span>{formatDisplayValue(incomeSummary.totalRecoveryIncome?.totalValuePerMwPerYear || 0, true, 'currency')}</span>,
            };
        } else {
            MODEL_SPECIFIC_COLUMN = {
                accessorKey: 'recoveryValuePerKwPerYear',
                id: 'recoveryValuePerKwPerYear',
                header: () => <span>$/kW/year</span>,
                cell: ({ row, getValue }) => (
                    <span className={tableValueClassName(getValue())}>
                        {formatDisplayValue(getValue(), true, 'currency')}
                    </span>
                ),
                footer: () => <span>{formatDisplayValue(incomeSummary.totalRecoveryIncome?.totalValuePerKwPerYear || 0, true, 'currency')}</span>,
            };
        }
    }

    const recoveryColumns: ColumnDef<IRecoveryEstimate>[] = [
        {
            accessorKey: 'name',
            id: 'name',
            header: () => <span className="pl-1">Recovery (avg. per tenant)</span>,
            cell: () => <span className="pl-1 text-dark">Recovery income</span>,
            footer: () => <span className="pl-1 text-nowrap">Total recovery income</span>,
        },
        {
            accessorKey: 'recoveryPercentage',
            id: 'recoveryPercentage',
            header: () => <span>Rec. %</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    numberType="percentage"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdate('recoveryPercentage');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter percentage"
                />
            ),
        },
        MODEL_SPECIFIC_COLUMN,
        {
            accessorKey: 'recoveryAmountPerYear',
            id: 'recoveryAmountPerYear',
            header: () => <span className="pr-1">Amount/year</span>,
            cell: ({ row, getValue }) => (
                <span className={`${tableValueClassName(getValue())} pr-1`}>
                    {formatDisplayValue(getValue(), false)}
                </span>
            ),
            footer: () => (
                <span className="pr-1">
                    {formatDisplayValue(incomeSummary.totalRecoveryIncome?.totalAmountPerYear || 0, showCents, 'currency')}
                </span>
            ),
        },
        ...recoveryCashFlowColumns,
    ];

    const table = useReactTable({
        data,
        columns: recoveryColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className={`NSTable EstimateRecoveryTable--${proForma.proFormaModelName?.replace(/ /g, '-')} mb-1`}>
            <thead className="NSTable__thead">
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="NSTable__thead__tr">
                        {headerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__thead__tr__th p-1">
                                {header.isPlaceholder ? null : (
                                    <div>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody
                className={classNames('NSTable__tbody', {
                    'NSTable__tbody--pro-forma-placeholder': !table.getRowModel().rows.length,
                })}
            >
                {table.getRowModel().rows.map(row => (
                    <tr key="estimate-recovery-row`" className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id} className="NSTable__tbody__tr__td p-1">
                                {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                )}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default EstimateRecoveryTable;
